import { useState, useCallback, useEffect, useRef, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import SearchInput from './components/SearchInput'
import DropDown from '../../shared/components/DropDown'
import { DropdownProvider } from '../../shared/utils/DropdownContext'
import Button from '../../shared/components/Button'
import { ProductCard } from './components'
import { ProductDetailsModal } from './modals'
import { useProducts, useDraftProducts, useDeleteProduct, usePublishProduct } from './hooks/useProducts'
import { useCategories } from './hooks/useMetaData'
import type { Product, ProductsParams } from './types'
import { usePermissions, Permission } from '../../shared/hooks/usePermissions'
import { useAuthStore } from '../auth/store'
import { useMerchants } from '../merchants/hooks/useMerchants'

const SCROLL_KEY = 'products-scroll-position'

const Products=()=> {
    const navigate = useNavigate()
    const { hasPermission, canAccessDrafts } = usePermissions()
    const { data: categoriesData } = useCategories()

    const { user } = useAuthStore()
    const isCollectiveStaff = user?.roleScope === 'COLLECTIVE'
    const { data: merchantsData } = useMerchants()

    const merchantMap = useMemo(() => {
        if (!isCollectiveStaff || !merchantsData) return undefined
        return new Map<string, string>(merchantsData.map(m => [m.id, m.name]))
    }, [isCollectiveStaff, merchantsData])
    const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false)
    const [selectedProduct, setSelectedProduct] = useState<Product>()
    const [activeTab, setActiveTab] = useState<'published' | 'drafts'>('published')
    const [stockFilter, setStockFilter] = useState<'all' | 'in-stock' | 'low-stock' | 'out-of-stock'>('all')
    const [allProducts, setAllProducts] = useState<Product[]>([])
    const deleteProductMutation = useDeleteProduct()
    const publishProductMutation = usePublishProduct()
    const [cursor, setCursor] = useState<string | undefined>(undefined)
    const [hasMore, setHasMore] = useState(true)
    const [isLoadingMore, setIsLoadingMore] = useState(false)
    const [filters, setFilters] = useState<ProductsParams>({
        'page[size]': 12,
        'filter[name]': '',
        'filter[category.name]': '',
        sort: []
    })
    const sentinelRef = useRef<HTMLDivElement>(null)

    // Permission checks
    const canCreate = hasPermission(Permission.PRODUCT_CREATE)
    const canUpdate = hasPermission(Permission.PRODUCT_UPDATE)
    const canDelete = hasPermission(Permission.PRODUCT_DELETE)
    const canPublish = hasPermission(Permission.PRODUCT_PUBLISH)
    const showDraftsTab = canAccessDrafts()

    // Fetch initial data
    const { data: publishedProductsData, isLoading: isLoadingPublished, error: publishedError } = useProducts({
        ...filters,
        'page[after]': cursor
    })
    const { data: draftProductsData, isLoading: isLoadingDrafts, error: draftError } = useDraftProducts({
        ...filters,
        'page[after]': cursor
    })

    // Stock calculation function for display only
    const getProductStockStatus = (product: Product): 'in-stock' | 'low-stock' | 'out-of-stock' => {
        const totalQuantity = product?.sizes?.reduce((total, size) => {
            return total + (size?.materials?.reduce((matTotal, material) => {
                return matTotal + (material?.colours?.reduce((colTotal, colour) => {
                    return colTotal + (colour?.quantity || 0)
                }, 0) || 0)
            }, 0) || 0)
        }, 0) || 0

        if (totalQuantity < 1) return 'out-of-stock'
        if (totalQuantity < 10) return 'low-stock'
        return 'in-stock'
    }

    // Accumulate products for infinite scroll
    useEffect(() => {
        const currentData = activeTab === 'published' ? publishedProductsData : draftProductsData
        const currentLoading = activeTab === 'published' ? isLoadingPublished : isLoadingDrafts
        
        if (currentData?.data && Array.isArray(currentData.data)) {
            if (cursor) {
                // Append new products (avoid duplicates)
                setAllProducts(prev => {
                    const newProducts = currentData.data.filter(newProduct =>
                        !prev.some(existingProduct => existingProduct.id === newProduct.id)
                    )
                    return [...prev, ...newProducts]
                })
                setIsLoadingMore(false)
            } else {
                // Reset with new products (first load or filter change)
                setAllProducts(currentData.data)
            }
            setHasMore(currentData.meta?.hasNextPage === true)
        } else if (!currentLoading && currentData?.data && currentData.data.length === 0) {
            setAllProducts([])
            setHasMore(false)
        }
    }, [publishedProductsData, draftProductsData, activeTab, cursor, isLoadingPublished, isLoadingDrafts])

    // Apply client-side stock filter
    const filteredProducts = stockFilter === 'all'
        ? allProducts
        : allProducts.filter(product => getProductStockStatus(product) === stockFilter)

    const isLoading = activeTab === 'published' ? isLoadingPublished : isLoadingDrafts
    const error = activeTab === 'published' ? publishedError : draftError
    const meta = activeTab === 'published' ? publishedProductsData?.meta : draftProductsData?.meta



    // Restore scroll position on mount
    useEffect(() => {
        const saved = sessionStorage.getItem(SCROLL_KEY)
        if (saved) {
            window.scrollTo(0, parseInt(saved))
            sessionStorage.removeItem(SCROLL_KEY)
        }
    }, [])

    // IntersectionObserver for infinite scroll
    useEffect(() => {
        if (!sentinelRef.current) return
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && hasMore && !isLoading && !isLoadingMore && meta?.endCursor) {
                    setIsLoadingMore(true)
                    setCursor(meta.endCursor)
                }
            },
            { threshold: 0.1 }
        )
        observer.observe(sentinelRef.current)
        return () => observer.disconnect()
    }, [hasMore, isLoading, isLoadingMore, meta?.endCursor])

    const handleEdit = (id: string) => {
        sessionStorage.setItem(SCROLL_KEY, window.scrollY.toString())
        const product = filteredProducts.find(p => p.id === id)
        const status = product?.status || 'PUBLISHED'
        navigate(`/products/edit/${id}?status=${status}`)
    }

    const handleDelete = (id: string) => {
        const product = filteredProducts.find(p => p.id === id)
        const isDraft = product?.status === 'DRAFT'

        deleteProductMutation.mutate({ id, isDraft }, {
            onSuccess: () => {
                if (selectedProduct?.id === id) {
                    setIsDetailsModalOpen(false)
                    setSelectedProduct(undefined)
                }
            }
        })
    }

    const handlePublish = (id: string) => {
        publishProductMutation.mutate(id, {
            onSuccess: () => {
                if (selectedProduct?.id === id) {
                    setIsDetailsModalOpen(false)
                    setSelectedProduct(undefined)
                }
            }
        })
    }

    const handleView = (id: string) => {
        const product = filteredProducts.find(p => p.id === id)
        if (product) {
            setSelectedProduct(product)
            setIsDetailsModalOpen(true)
        }
    }

    const resetProducts = () => {
        setCursor(undefined)
        setHasMore(true)
        setIsLoadingMore(false)
        // Don't reset allProducts here - let the useEffect handle it
    }

    const handleSearch = useCallback((searchTerm: string) => {
        setFilters(prev => ({ ...prev, 'filter[name]': searchTerm }))
        resetProducts()
    }, [])

    const handleCategoryFilter = useCallback((category: string) => {
        setFilters(prev => ({ ...prev, 'filter[category.name]': category }))
        resetProducts()
    }, [])

    const handleStockFilter = useCallback((stockStatus: string) => {
        setStockFilter(stockStatus as 'all' | 'in-stock' | 'low-stock' | 'out-of-stock')
    }, [])

    const handleTabChange = useCallback((tab: 'published' | 'drafts') => {
        setActiveTab(tab)
        setStockFilter('all')
        setFilters(prev => ({ ...prev, 'filter[name]': '', 'filter[category.name]': '', sort: [] }))
        resetProducts()
    }, [])

  return (
    <DropdownProvider>
      <div className='py-10'>
          {/* Tabs */}
          <div className='flex border-b border-border mb-6'>
              <button
                  className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
                      activeTab === 'published'
                          ? 'border-blue-500 text-blue-600'
                          : 'border-transparent text-muted-foreground hover:text-foreground'
                  }`}
                  onClick={() => handleTabChange('published')}
              >
                      Published Products ({publishedProductsData?.meta?.totalCount || 0})
              </button>
              {/* Only show drafts tab if user can access drafts (has PRODUCT_READ_ALL permission) */}
              {showDraftsTab && (
                  <button
                      className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
                          activeTab === 'drafts'
                              ? 'border-blue-500 text-blue-600'
                              : 'border-transparent text-muted-foreground hover:text-foreground'
                      }`}
                      onClick={() => handleTabChange('drafts')}
                  >
                          Draft Products ({draftProductsData?.meta?.totalCount || 0})
                  </button>
              )}
          </div>

          <div className='flex flex-wrap lg:flex-nowrap items-center justify-between gap-3 lg:gap-0'>
              <SearchInput 
                  className='w-full lg:w-[313px]'
                  onSearch={handleSearch}
                  placeholder="Search products by name..."
              />
                  <DropDown
                      options={[
                          { title: 'All Stock', value: 'all' },
                          { title: 'In Stock', value: 'in-stock' },
                          { title: 'Low Stock', value: 'low-stock' },
                          { title: 'Out of Stock', value: 'out-of-stock' }
                      ]}
                      className='w-[calc(50%-6px)] lg:w-[166px]'
                      onSelect={handleStockFilter}
                      placeholder="Stock Status"
                  />
                  {/* <DropDown options={Data.statuses} className='w-[166px]' /> */}
              <DropDown 
                  options={[
                      { title: 'All Categories', value: '' },
                      ...(categoriesData?.data || []).map(cat => ({ title: cat.name, value: cat.name }))
                  ]} 
                  className='w-[calc(50%-6px)] lg:w-[166px]'
                  onSelect={handleCategoryFilter}
                  placeholder="Category"
              />
              {/* Only show Add Product button if user has create permission */}
              {canCreate && (
                  <Button
                      className='w-full lg:w-[182px]'
                      onClick={() => navigate('/products/add')}
                      icon={<svg width="25" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M12.5 5V19" stroke="#FBFBFE" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          <path d="M5.5 12H19.5" stroke="#FBFBFE" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      }
                      iconPosition='left'
                  >
                      Add product
                  </Button>
              )}
          </div>

              <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-6 mt-8 justify-items-center lg:justify-items-start'>
                  {isLoading && filteredProducts.length === 0 ? (
                      <div className="col-span-full text-center py-8">Loading products...</div>
                  ) : error ? (
                      <div className="col-span-full text-center py-8 text-red-500">Error loading products</div>
                      ) : filteredProducts.length === 0 ? (
                      <div className="col-span-full text-center py-8">No products found</div>
                  ) : (
                                  filteredProducts.map((product) => (
                          <ProductCard
                              key={product.id}
                              id={product.id}
                                          name={product?.name || 'Untitled Product'}
                                          description={product?.description || 'No description available'}
                                          basePrice={product?.basePrice ?? 0}
                                          sellingPrice={product?.sellingPrice ?? 0}
                                          publishStatus={product?.status === "PUBLISHED" ? 'Published' : 'Draft'}
                                          productType={product?.category?.name || 'Uncategorized'}
                                          inStock={getProductStockStatus(product)}
                                          merchantName={product.merchantId ? merchantMap?.get(product.merchantId) : undefined}
                                          image={(() => {
                                              const firstColour = product?.sizes?.[0]?.materials?.[0]?.colours?.[0]
                                              if (!firstColour) return undefined
                                              
                                              // Combine imageUrls (backend) and images (newly added)
                                              const allImages = [
                                                  ...(firstColour.imageUrls || []),
                                                  ...(firstColour.images || []).map(img => 
                                                      typeof img === 'string' ? img : URL.createObjectURL(img)
                                                  )
                                              ]
                                              
                                              return allImages[0]
                                          })()}
                                          stockQuantity={product?.sizes?.reduce((total, size) => {
                                              return total + (size?.materials?.reduce((matTotal, material) => {
                                                  return matTotal + (material?.colours?.reduce((colTotal, colour) => {
                                                      return colTotal + (colour?.quantity || 0)
                                                  }, 0) || 0)
                                              }, 0) || 0)
                                          }, 0) || 0}
                                          sizes={product?.sizes?.map(v => v?.size?.name).filter(Boolean) || []}
                                          colors={product?.sizes?.[0]?.materials?.[0]?.colours?.map(c => c?.colour?.hexValue).filter(Boolean) || []}
                              onView={handleView}
                              onEdit={canUpdate ? handleEdit : undefined}
                              onDelete={canDelete ? handleDelete : undefined}
                              onPublish={canPublish ? handlePublish : undefined}
                              isDeleting={deleteProductMutation.isPending && deleteProductMutation.variables?.id === product.id}
                              isPublishing={publishProductMutation.isPending && publishProductMutation.variables === product.id}
                          />
                      ))
                  )}
              </div>

              {/* Infinite scroll sentinel + loading indicator */}
              <div ref={sentinelRef} className="flex justify-center py-8">
                  {(isLoadingMore || (isLoading && filteredProducts.length > 0)) && (
                      <div className="w-8 h-8 border-2 border-border border-t-gray-800 rounded-full animate-spin" />
                  )}
              </div>

              <ProductDetailsModal
                  isOpen={isDetailsModalOpen}
                  onClose={() => setIsDetailsModalOpen(false)}
                  product={selectedProduct}
                  onEdit={canUpdate ? handleEdit : undefined}
                  onPublish={canPublish ? handlePublish : undefined}
                  onDelete={canDelete ? handleDelete : undefined}
                  isPublishing={publishProductMutation.isPending}
                  isDeleting={deleteProductMutation.isPending && deleteProductMutation.variables?.id === selectedProduct?.id}
              />
      </div>
    </DropdownProvider>
  )
}

export default Products

